import type { GridProps } from '@mui/material';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { COLORS } from 'muiTheme/colors';
import { SIZES, SPACING } from 'muiTheme/MuiDataGrid';
import type { MouseEventHandler, ReactNode } from 'react';
import { useEffect } from 'react';
import { TotalRowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { getRoundedValueTooltipTitle } from '../components/CustomTableCell';
import { formatPriceWithDollarSign, roundDecimalValue } from '../helper';
import { getRowClassName } from '../../../../../../muiTheme/dataGridUtils';
import { updatePricingTotal } from '../../../../grid/reduxStore/editorSlice.ts';
import { useAppDispatch } from '../../../../grid/reduxStore/Store.ts';

const pseudoElement = {
  content: "''",
  display: 'block',
  position: 'absolute',
  width: '100%',
  height: '2px',
};

const FooterRowWrapper = styled(Grid)(() => ({
  columnSpacing: 2,
  minHeight: SIZES.defaultRowHeight,
  justifyContent: 'center',
  alignItems: 'stretch',
  borderLeft: `1px solid ${COLORS.divider}`,
  borderRight: `1px solid ${COLORS.divider}`,
  position: 'relative',
  backgroundColor: 'var(--table-row-default-background-color)',
  '&.py-hide-total-row, &.py-hide-total-row:hover': {
    backgroundColor: COLORS.greyedBackgroundColor,
  },
  '&.selected-row, &.selected-row + .unselected-row': {
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30), 0px 1px 3px rgba(0, 0, 0, 0.15)',
  },
  '&.selected-row': {
    '.py-active-row': {
      '&::before': {
        ...pseudoElement,
        backgroundColor: `${COLORS.HoverNFocus}`,
        top: 0,
        left: 0,
      },
      '&::after': {
        ...pseudoElement,
        backgroundColor: `${COLORS.HoverNFocus}`,
        bottom: 1,
        left: 0,
      },
      borderRight: `2px solid ${COLORS.HoverNFocus}`,
      borderLeft: `2px solid ${COLORS.HoverNFocus}`,
      position: 'relative',
    },
  },
  '&::before, &::after': { ...pseudoElement },
  '&:hover::before, &:hover::after': {
    backgroundColor: COLORS.HoverNFocus,
  },
  '&::before': { top: 0 },
  '&::after': {
    bottom: 0,
    backgroundColor: 'transparent',
  },
  '&.py-total-row': {
    borderTop: `1px solid ${COLORS.divider}`,
    borderBottom: `1px solid ${COLORS.divider}`,
    '&::after': { display: 'none' },
  },
}));

const FooterRowItem = ({
  children,
  testId,
  onClick,
  className,
  ...props
}: {
  children?: ReactNode;
  testId?: string;
  className?: string;
  onClick?: MouseEventHandler;
} & GridProps) => (
  <Grid
    item
    md={3}
    alignItems="center"
    className={className}
    data-testid={testId}
    display="flex"
    justifyContent="flex-end"
    onClick={onClick}
    textAlign="right"
    width="100%"
    position="relative"
    {...props}
  >
    {children}
  </Grid>
);

type PricingTableTotalFooterRowProps = {
  rowType: TotalRowTypesIdentifier;
  subtotalValue: number;
  onClick?: MouseEventHandler;
  blockId?: string;
  sectionId?: string;
  isRowSelected: boolean;
  priceValue: string;
  pricingDecimalPlaces: number;
  calcInputComponent: ReactNode;
  titleComponent?: ReactNode;
  activeCurrencySymbol?: string;
  menuTrigger?: ReactNode;
  onValueCellClick?: MouseEventHandler;
  // Distinct testid base for derived display rows that reuse a persisted rowType
  // for styling (e.g. frequency subtotals) — keeps rowType-keyed testids unique.
  testIdOverride?: string;
  // Full data-testid overrides for the row wrapper and value cell, for callers
  // that need to source these from their own *.test-ids.ts constant.
  rowTestId?: string;
  valueTestId?: string;
};

type FooterRowItem = {
  key: string;
  testId?: string;
  children?: ReactNode;
  className?: string;
  props?: GridProps;
};

const PricingTableTotalFooterRow = ({
  rowType,
  subtotalValue,
  onClick,
  isRowSelected,
  priceValue,
  pricingDecimalPlaces,
  calcInputComponent,
  titleComponent,
  blockId,
  sectionId,
  activeCurrencySymbol,
  menuTrigger,
  onValueCellClick,
  testIdOverride,
  rowTestId,
  valueTestId,
}: PricingTableTotalFooterRowProps) => {
  const dispatch = useAppDispatch();
  const computeFormattedValue = () => {
    const hasValidSubtotalAndPrice = typeof subtotalValue === 'number' && !Number.isNaN(subtotalValue) && priceValue !== '';
    const isCalculatedRow = rowType === TotalRowTypesIdentifier.SUBTOTAL || rowType === TotalRowTypesIdentifier.TOTAL;
    const shouldFormat = isCalculatedRow || hasValidSubtotalAndPrice;

    return shouldFormat ? formatPriceWithDollarSign(subtotalValue, pricingDecimalPlaces, activeCurrencySymbol) : '';
  };

  const testIdValue = testIdOverride ?? rowType?.toLowerCase() ?? 'unknown';

  const valueItem: FooterRowItem = {
    key: 'value',
    testId: valueTestId ?? `value-${testIdValue}`,
    className: 'py-footer-cell',
    children: (
      <>
        <Tooltip
          data-testid="footer-cell-tooltip"
          title={getRoundedValueTooltipTitle({ value: subtotalValue, numberOfDecimalPlaces: pricingDecimalPlaces })}
          placement="bottom"
          followCursor
        >
          <span className="py-numeric-cell-value">{computeFormattedValue()}</span>
        </Tooltip>
        {menuTrigger}
      </>
    ),
    props: { paddingLeft: SPACING.sm, paddingRight: SPACING.sm, onClick: onValueCellClick },
  };

  const footerItems: FooterRowItem[] = [
    {
      key: 'title',
      testId: `title-${testIdValue}`,
      className: 'py-footer-cell py-footer-title-cell',
      children: (
        <>
          {menuTrigger}
          {titleComponent}
          {calcInputComponent}
        </>
      ),
      props: { md: 9.2 },
    },
    { ...valueItem, props: { ...valueItem.props, md: 2.8 } },
  ];

  useEffect(() => {
    if (rowType === TotalRowTypesIdentifier.TOTAL && sectionId && blockId) {
      const updatePricingTotalValue = roundDecimalValue(subtotalValue, pricingDecimalPlaces);
      dispatch(updatePricingTotal({ sectionId, blockId, total: +updatePricingTotalValue }));
    }
  }, [subtotalValue, pricingDecimalPlaces, rowType, sectionId, blockId]);

  return (
    <FooterRowWrapper
      container
      data-testid={rowTestId ?? `${testIdValue}-row`}
      onClick={onClick}
      className={`${isRowSelected ? 'selected-row' : 'unselected-row'} ${getRowClassName(rowType)} `}
    >
      {footerItems.map(({ key, testId, children, className, props = {} }) => (
        <FooterRowItem className={className} key={key} testId={testId} {...props}>
          {children}
        </FooterRowItem>
      ))}
    </FooterRowWrapper>
  );
};

export default PricingTableTotalFooterRow;
